/**
 * A short record of what the app did, for when something misbehaves on a
 * phone that cannot be inspected.
 *
 * "It marks it and then unmarks it" took a live account and a stopwatch to
 * explain. This is the cheaper route next time: the app writes down what it
 * tried, what came back, and how long it took, and the settings screen hands
 * it over as text.
 *
 * Deliberately small and deliberately dull. It records actions and outcomes,
 * never show titles beyond an id, and never anything from the account it does
 * not already display on screen — a log that has to be redacted before it can
 * be sent is a log nobody sends.
 */
import { META_STORE, withStore } from "@/store/db";

/** Swallows a failure the caller has already accounted for. */
function ignore(): void {}

const LOG_KEY = "debug-log";

/** Enough to cover a session's worth of fiddling, small enough to paste. */
const MAX_ENTRIES = 300;

export interface LogEntry {
  at: string;
  event: string;
  detail: string;
}

let buffer: LogEntry[] = [];
let loaded = false;
let writing: Promise<void> | null = null;

async function load(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const [stored] = await withStore<LogEntry[] | undefined>(
      META_STORE,
      "readonly",
      (store) => store.get(LOG_KEY) as IDBRequest<LogEntry[] | undefined>,
    );
    buffer = stored ?? [];
  } catch {
    buffer = [];
  }
}

/** Coalesced: a burst of entries costs one write, not one write each. */
function flush(): void {
  writing ??= new Promise<void>((resolve) => {
    setTimeout(() => {
      const snapshot = buffer.slice(-MAX_ENTRIES);
      void withStore(META_STORE, "readwrite", (store) => store.put(snapshot, LOG_KEY))
        .catch(ignore)
        .finally(() => {
          writing = null;
          resolve();
        });
    }, 400);
  });
}

export function log(event: string, detail: unknown = ""): void {
  const text =
    typeof detail === "string" ? detail : JSON.stringify(detail, undefined, 0).slice(0, 300);
  buffer.push({ at: new Date().toISOString(), event, detail: text });
  if (buffer.length > MAX_ENTRIES * 2) buffer = buffer.slice(-MAX_ENTRIES);
  void load().then(flush);
}

export async function readLog(): Promise<LogEntry[]> {
  await load();
  return buffer.slice(-MAX_ENTRIES);
}

export async function clearLog(): Promise<void> {
  await load();
  buffer = [];
  await withStore(META_STORE, "readwrite", (store) => store.put([], LOG_KEY)).catch(ignore);
}

/** One line per entry, with the clock relative to the first, which is what reads. */
export function formatLog(entries: readonly LogEntry[]): string {
  const first = entries[0] === undefined ? 0 : Date.parse(entries[0].at);
  const lines = entries.map((entry) => {
    const offset = ((Date.parse(entry.at) - first) / 1000).toFixed(2).padStart(8);
    return `${offset}s  ${entry.event}${entry.detail === "" ? "" : `  ${entry.detail}`}`;
  });
  return [`tv-tracker log, ${String(entries.length)} entries`, ...lines].join("\n");
}
